import React, { memo } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import Download from '@mui/icons-material/Download';
import MoreVert from '@mui/icons-material/MoreVert';
import JobStatusChip from './JobStatusChip';
import { CreatedCell, JobIdCell, JobTypeCell, PartnerCell } from './JobRowCells';
import { BusyAction } from './hooks/useJobActions';
import { DashboardJob, formatDuration, hasFiles, shortId } from './jobModel';

export interface JobRowHandlers {
  onToggle: (id: string) => void;
  onOpen: (id: string) => void;
  onOpenMenu: (id: string, anchor: HTMLElement) => void;
  onDownload: (job: DashboardJob) => void;
  onCopyId: (id: string) => void;
}

interface JobRowProps extends JobRowHandlers {
  job: DashboardJob;
  selected: boolean;
  busy?: BusyAction;
}

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

/** Memoized: re-renders only when its job object, selection or busy state changes (handlers are stable). */
const JobRow: React.FC<JobRowProps> = ({ job, selected, busy, onToggle, onOpen, onOpenMenu, onDownload, onCopyId }) => {
  const downloadable = hasFiles(job);
  return (
    <TableRow
      hover
      selected={selected}
      onClick={() => onOpen(job.id)}
      sx={{ cursor: 'pointer', '& > td': { py: 1.25 } }}
      data-testid={`job-row-${job.id}`}
    >
      <TableCell padding="checkbox" onClick={stop}>
        <Checkbox
          checked={selected}
          onChange={() => onToggle(job.id)}
          inputProps={{ 'aria-label': `Select job ${shortId(job.id)}` }}
        />
      </TableCell>
      <TableCell>
        <JobIdCell id={job.id} userId={job.userId} onCopy={onCopyId} />
      </TableCell>
      <TableCell>
        <PartnerCell name={job.partnerName} />
      </TableCell>
      <TableCell>
        <JobTypeCell kind={job.kind} label={job.typeLabel} detail={job.typeDetail} />
      </TableCell>
      <TableCell>
        <JobStatusChip status={job.status} />
      </TableCell>
      <TableCell>
        <CreatedCell createdAt={job.createdAt} />
      </TableCell>
      <TableCell sx={{ fontSize: 13, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{formatDuration(job)}</TableCell>
      <TableCell>
        <Box
          component="span"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13, color: job.files.length ? 'text.secondary' : 'text.disabled' }}
        >
          <DescriptionOutlined sx={{ fontSize: 17 }} />
          {job.files.length}
        </Box>
      </TableCell>
      <TableCell align="right" onClick={stop}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }}>
          <Tooltip title={downloadable ? 'Download files' : 'No files yet'}>
            <span>
              <IconButton
                aria-label="Download files"
                color="primary"
                disabled={!downloadable || busy === 'download'}
                onClick={() => onDownload(job)}
              >
                {busy === 'download' ? <CircularProgress size={18} /> : <Download fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <IconButton
            aria-label="More actions"
            aria-haspopup="menu"
            disabled={busy === 'delete'}
            onClick={(e) => onOpenMenu(job.id, e.currentTarget)}
          >
            {busy === 'delete' ? <CircularProgress size={18} /> : <MoreVert fontSize="small" />}
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default memo(JobRow);
